import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

const PerformanceAlertsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['perf-monitoring-alerts'],
    queryFn: () => apiService.getPerformanceAlerts({ open: true, limit: 200 }),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });

  const ackMutation = useMutation({
    mutationFn: (id: number) => apiService.acknowledgePerformanceAlert(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['perf-monitoring-alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['perf-monitoring-dashboard'] });
    },
  });

  const err = error as { response?: { data?: { error?: string } } } | null;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Alertes performance
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refetch()} disabled={isFetching}>
          Actualiser
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" paragraph>
        Déclenchées lorsqu&apos;un seuil est dépassé (temps, mémoire, SQL, HTTP, images, erreurs HTTP/exception).
      </Typography>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err.response?.data?.error ?? 'Impossible de charger les alertes.'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell align="right">Seuil</TableCell>
                  <TableCell align="right">Réel</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.items ?? []).map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell>{alert.created_at}</TableCell>
                    <TableCell>
                      <Chip size="small" label={alert.threshold_type} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{alert.route}</TableCell>
                    <TableCell>{alert.message}</TableCell>
                    <TableCell align="right">{alert.threshold_value}</TableCell>
                    <TableCell align="right">{alert.actual_value}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => ackMutation.mutate(alert.id)}
                        disabled={ackMutation.isPending}
                      >
                        Acquitter
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(data?.items ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Aucune alerte ouverte.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PerformanceAlertsPage;
